import annotateRegisteredSequences from 'antibodies/domain-annotation/annotateRegisteredSequences';
import {
  AnnotationSubmitDecision,
  AnnotationSubmitDecisionType,
} from 'antibodies/domain-annotation/DomainAnnotationTypes';
import promptForSequenceAnnotationConfig from 'antibodies/domain-annotation/promptForSequenceAnnotationConfig';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ClientId} from 'notebook/ELNConstants';
import {
  doPostRegistrationRefresh,
  RegistrationActionOrigin,
  runRegistrationFlow,
} from 'notebook/integrations/registry/RegistrationActionUtil';
import {RegistrationStructuredTableAdapter} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata,
  RegistrationTableInsertType,
} from 'notebook/integrations/registry/RegistrationTableConstants';
import {ACTION_REGISTER} from 'notebook/store/VersioningConstants';
import makeSimpleActionFunction, {
  getSubmitActionFunctionIcon,
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
  ServerSideWriteActionResult,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import isMissingFieldColumn from 'notebook/structured-tables/columns/isMissingFieldColumn';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {StructuredTableSubmissionScope} from 'notebook/structured-tables/StructuredTableApiConstants';
import {
  GenericStructuredTableColumn,
  StructuredTableRow,
  StructuredTableSchema,
  TableActionFunctionParameters,
} from 'notebook/structured-tables/StructuredTableConstants';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import {cellHasUnsubmittedChanges} from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell, EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {
  areTypedValuesEquivalent,
  getBioentityInfoFromTypedValue,
  isTypedValueEmpty,
} from 'notebook/tables/typed-values/DataTypes';
import {confirmExpirationDateEdits} from 'samples/valid-contents/ConfirmEntitiesExpirationDate';
import {EXPIRATION_DATE_FIELD_DISPLAY_NAME} from 'samples/valid-contents/ValidContentsConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import APIUtil from 'util/APIUtil';

import {RowSubmissionRange} from './makeSubmitRowActionFunction';

function isRegisterExistingEntities(
  params: TableActionFunctionParameters<RegistrationStructuredTableMetadata, {}>
): boolean {
  return (
    params.structuredTable.metadata.insertType === RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES
  );
}

function areAllExistingBioentitiesPresent(
  params: TableActionFunctionParameters<RegistrationStructuredTableMetadata, {}>
): boolean {
  const existingBioentityColumnIndex = params.structuredTable.columns.findIndex(
    ({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY
  );
  return params.computedTable.every((row) => !isTypedValueEmpty(row[existingBioentityColumnIndex].value));
}

/**
 * In addition to disabling our submit button when we normally would disable
 * it (e.g. isTableActionButtonDisabled), we also want to disable submitting when
 * we are attempting to register existing entities and we haven't filled out
 * an entity in each row.
 */
function registrationTableIsSubmitDisabled(
  params: TableActionFunctionParameters<RegistrationStructuredTableMetadata, {}>
): boolean {
  return (
    isTableActionButtonDisabled(params) ||
    (isRegisterExistingEntities(params) && !areAllExistingBioentitiesPresent(params)) ||
    !isValidLabelingStrategy(params)
  );
}

function registrationTableSubmitTooltip(
  params: TableActionFunctionParameters<RegistrationStructuredTableMetadata, {}>
): string | null | JSX.Element {
  if (isRegisterExistingEntities(params) && !areAllExistingBioentitiesPresent(params)) {
    return 'Table contains row(s) with nonexisting entities.';
  } else if (!isValidLabelingStrategy(params)) {
    return 'Invalid Naming and Registry ID Setting';
  } else {
    return tableActionButtonDisabledTooltip(params);
  }
}

/**
 * Helper function to determine if the current stored labeling strategy is still valid for the associated
 * tagSchema. Also double checked by registration.py
 */
function isValidLabelingStrategy(
  params: TableActionFunctionParameters<RegistrationStructuredTableMetadata, {}>
): boolean {
  const tagSchema = (params.structuredTableAdapter as RegistrationStructuredTableAdapter).getTagSchema(
    params.structuredTable
  );
  if (!tagSchema) {
    return true;
  }
  return tagSchema?.labelingStrategies.includes(params.structuredTable.metadata.labelingStrategy);
}

/**
 * Get ids of bioentities that have an expiration date updates.
 *
 * Includes re-submitting finalized rows with updates to an expiration date field as well as submitting a
 * "register existing entities" table with updates to the entity's expiration date.
 * These ids will be used to query for any containers inheriting the expiration date to pop up a confirmation
 * modal. The check for whether expiration tracking is actually enabled (vs just happening to have an "Expiration
 * date" field) occurs downstream as well.
 *
 * @param structuredTableRows Structured table rows, to parse for values
 * @param structuredTableColumns Columns of the table, to find expiration date & primary bioentity columns
 * @param structuredTableSchemas The tag schema will be the first schema
 * @param computedTable Computed table, to parse for values
 * @param metadata Table metadata, including what type of table (register new vs existing) it is
 *
 * @return list of bioentity api ids
 */
export function getBioentityIdsForExpirationConfirmationFromStructuredTable({
  structuredTableRows,
  structuredTableColumns,
  structuredTableSchemas,
  computedTable,
  metadata,
}: {
  structuredTableRows: Array<StructuredTableRow<RegistrationStructuredTableRowMetadata>>;
  structuredTableColumns: Array<GenericStructuredTableColumn<ClientId>>;
  structuredTableSchemas: Array<StructuredTableSchema>;
  computedTable: EvaluatedTable;
  metadata: RegistrationStructuredTableMetadata;
}): Array<string> {
  if (!BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE) {
    return [];
  }
  // We need an expiration date schema field column & the primary bioentity column to proceed
  const expirationColumnIdx = structuredTableColumns.findIndex(
    (column) =>
      column.columnType === TableColumnType.TAG_VALUE && column.name === EXPIRATION_DATE_FIELD_DISPLAY_NAME
  );
  const primaryColumnIdx = structuredTableColumns.findIndex(
    (column) => column.columnType === TableColumnType.EXISTING_BIOENTITY
  );
  if (expirationColumnIdx === -1 || primaryColumnIdx === -1) {
    return [];
  }
  const expirationColumn = structuredTableColumns[expirationColumnIdx];

  if (isMissingFieldColumn(expirationColumn, structuredTableSchemas)) {
    return [];
  }

  return computedTable.flatMap((row, rowIndex) => {
    const bioentityIds: Array<string> = [];
    const cell = row[expirationColumnIdx];
    const {isFinalized, lastSyncedUpstreamValues} = structuredTableRows[rowIndex];

    // If we are resubmitting a finalized row, check for unsubmitted changes in the expiration date cell
    const isResubmittingFinalizedRowWithChange =
      isFinalized &&
      lastSyncedUpstreamValues != null &&
      cellHasUnsubmittedChanges(cell, lastSyncedUpstreamValues, expirationColumn, structuredTableSchemas);

    // If we are submitting an unfinalized row with an existing bioentity, check for differences between the
    // resolved value (looked up) and the actual value being submitted
    const isSubmittingExistingBioentityWithChange =
      !isFinalized &&
      metadata.insertType === RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES &&
      // No resolutions from custom eval (value replaced) or value no longer matches
      ((cell.resolutions ?? []).length === 0 ||
        !areTypedValuesEquivalent(cell.resolutions![0].resolvedValue, cell.value));

    // In either case, add the relevant bioentity to check for downstream containers
    if (isResubmittingFinalizedRowWithChange || isSubmittingExistingBioentityWithChange) {
      const typedValue =
        row[primaryColumnIdx].value ?? structuredTableRows[rowIndex].cells[primaryColumnIdx].typedValue;
      const bioentityInfo = typedValue ? getBioentityInfoFromTypedValue(typedValue) : null;
      if (bioentityInfo) {
        bioentityIds.push(bioentityInfo.id);
      }
    }
    return bioentityIds;
  });
}

export async function onInvokeRegisterActionFunction({
  entryOrTemplateId,
  structuredTable,
  structuredTableAdapter,
  computedTable,
  actionFunctionContext,
  table,
}: TableActionFunctionParameters<RegistrationStructuredTableMetadata, {}>): Promise<void> {
  assert(
    entryOrTemplateId != null && APIUtil.isBaseEntry(entryOrTemplateId),
    `Registration action is only supported in entries. Got: ${entryOrTemplateId}`
  );
  // @ts-expect-error: for some reason, using `as` or `!` causes ESLint to crash here
  const tagSchema = structuredTableAdapter.getTagSchema(structuredTable);
  assert(tagSchema != null, 'Registration table was submitted without a schema');

  // If expiration date is being modified for entities, and there are existing containers of those entities,
  // only proceed if user confirms the action
  const bioentityIds = getBioentityIdsForExpirationConfirmationFromStructuredTable({
    structuredTableRows: structuredTable.rows,
    structuredTableColumns: structuredTable.columns,
    structuredTableSchemas: structuredTable.schemas,
    computedTable,
    metadata: structuredTable.metadata,
  });
  const loadingScreenMessage = 'Registering...';
  // The submission sends the table as it was when the user clicked, so the loading screen blocks edits while the
  // pre-submit lookups are in flight. Confirmation modals hide it while they are open.
  LoadingScreen.show(loadingScreenMessage);
  let annotationDecision: AnnotationSubmitDecision;
  try {
    if (bioentityIds.length) {
      const {shouldProceed} = await confirmExpirationDateEdits(tagSchema.id, bioentityIds);
      if (!shouldProceed) {
        return;
      }
    }
    annotationDecision = await promptForSequenceAnnotationConfig({
      tagSchema,
      rows: structuredTable.rows,
      entryId: entryOrTemplateId,
    });
  } finally {
    LoadingScreen.hide();
  }
  if (annotationDecision.type === AnnotationSubmitDecisionType.CANCEL) {
    return;
  }
  await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.RegistrationSubmitResponse>({
    entryId: entryOrTemplateId,
    loadingScreenMessage,
    autoSaverAction: ACTION_REGISTER,
    doPerformAction: async ({
      updateStatus,
      dataModsHeader,
      allRowsSubmitted,
    }): Promise<ServerSideWriteActionResult<ActionFunctionDAO.RegistrationSubmitResponse>> =>
      await runRegistrationFlow(
        (
          allowMerging: boolean,
          selection?: RowSubmissionRange,
          evaluatedTableRows: Array<Array<EvaluatedCell>> = computedTable,
          latestVersionId?: number,
          submissionScope?: StructuredTableSubmissionScope
        ) =>
          ActionFunctionDAO.register(
            entryOrTemplateId,
            structuredTable,
            evaluatedTableRows,
            actionFunctionContext,
            selection,
            submissionScope,
            latestVersionId,
            {
              allowMerging,
            },
            dataModsHeader,
            allRowsSubmitted
          ),
        tagSchema,
        RegistrationActionOrigin.REGISTRATION_STRUCTURED_TABLE,
        undefined,
        structuredTable,
        computedTable,
        updateStatus
      ),
    doPostRefresh: async (actionResult) => {
      await doPostRegistrationRefresh(
        actionResult.responseData,
        entryOrTemplateId,
        structuredTable.provenanceUuid,
        tagSchema
      );
      invokeStructuredTableMappings(
        entryOrTemplateId,
        structuredTable,
        // on a partial success we want to map only the rows that were successfully submitted
        actionResult.resultType === ActionResultType.PARTIAL_SUCCESS ? actionResult.submittedRange : undefined
      );
      if (
        annotationDecision.type === AnnotationSubmitDecisionType.ANNOTATE &&
        actionResult.resultType === ActionResultType.SUCCESS
      ) {
        annotateRegisteredSequences({
          rows: structuredTable.rows,
          firstSubmittedRowIndex: 0,
          registeredEntityIds: actionResult.responseData.bioentityIds,
          unregisteredRowIds: annotationDecision.unregisteredRowIds,
          config: annotationDecision.config,
        });
      }
    },
    analyticsActionForPerfTracking: ELN_ANALYTICS_ACTIONS.REGISTERED_FROM_STRUCTURED_TABLE.action,
    structuredTables: [structuredTable],
    structuredTableAdapters: [structuredTableAdapter],
    computedTables: [computedTable],
    tables: table ? [table] : [],
  });
}

/**
 * An ActionFunction for registering entities in a StructuredTable.  It will be disabled if there
 * are any errors in the evaluated table or if all rows have been marked as finalized or if the
 * table is in a template.  Before calling the backend ActionFunction, this component will make sure
 * that the entry is flushed to the backend.  While it is performing the action, it will display a
 * loading screen and block any changes to the underlying entry by the client performing the action.
 */
export const registerActionFunction = makeSimpleActionFunction<RegistrationStructuredTableMetadata, {}>({
  label: (params) => getSubmitButtonLabel(params),
  icon: getSubmitActionFunctionIcon(),
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeRegisterActionFunction,
  getDisabled: registrationTableIsSubmitDisabled,
  getTooltip: registrationTableSubmitTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
